import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatSort, Sort } from '@angular/material/sort';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { DatarefSearchContent, DatarefSearchResult } from '@shared/models/search.model';
import { MatTableDataSource } from '@angular/material/table';
import { PAGE_SIZE } from '@shared/constants/page.constant';
import { Domain, Language, Market } from '@shared/models/dataref.model';
import { ResourceMedia } from '@shared/models/media.model';
import { takeUntil, tap } from 'rxjs/operators';
import { RefDataService } from '@services/ref-data.service';
import { ColumnConfiguration } from '@shared/models/generic/column-configuration.model';
import { DisplayData } from '@shared/components/data-table-manager/data-table-manager.component';
import { TranslateService } from '@ngx-translate/core';
import { TablePaginatorComponent } from '@shared/components/data-table-manager/table-paginator/table-paginator.component';
import { BaseSearchResult } from '@shared/components/base-search-result';

interface PartnerDetail {
  market: string;
  lang: string;
  name: string;
}

interface PartnerSearchContent extends DatarefSearchContent {
  details?: PartnerDetail[];
}

@Component({
  selector: 'app-partners-search-result',
  templateUrl: './partners-search-result.component.html',
  styleUrls: ['./partners-search-result.component.scss'],
  standalone: false,
})
export class PartnersSearchResultComponent extends BaseSearchResult implements OnInit, OnDestroy {
  @Input()
    domainsObs: Observable<Domain[]> = of();
  @Input()
    searchResult: Observable<DatarefSearchResult> = of();
  @Input()
    languages: Language[] = [];
  @Input()
    markets: Market[] = [];
  @Output()
    pageEmitter: EventEmitter<TablePaginatorComponent> = new EventEmitter<TablePaginatorComponent>();
  @Output()
    sortEmitter: EventEmitter<Sort> = new EventEmitter<Sort>();
  @ViewChild(MatSort) matSort: MatSort | null = null;

  columnConfigs: ColumnConfiguration<PartnerSearchContent>[] = [];
  dataSource: MatTableDataSource<DatarefSearchContent> = new MatTableDataSource();
  pageSize = PAGE_SIZE;
  pageIndex = 0;
  totalElements = 0;
  _sort?: Sort;
  domains: Domain[] = [];

  displayInfo: DisplayData = {
    resultsMessage: '',
    dataLength: 0,
    noDataMessage: '',
    totalPages: 0,
  };

  subscriptions: Subscription = new Subscription();
  destroy$ = new Subject<void>();

  constructor(
    private readonly refDataService: RefDataService,
    private readonly translateService: TranslateService,
  ) {
    super();
  }

  ngOnInit(): void {
    this._sort = { active: 'name', direction: 'asc' };

    this.domainsObs
      .pipe(
        tap((result) => {
          this.domains = result;
          this.createColumnConfig();
          if (this.dataSource.data.length > 0) {
            this.dataSource._updateChangeSubscription();
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
    this.searchResult
      .pipe(
        tap((result) => {
          if (result?.content && result.page) {
            this.enrichDatasource(result);
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  sortData(sortEvent: Sort): void {
    this._sort = sortEvent;
    this.sortEmitter.emit(sortEvent);
  }

  findDomain(key: string): Domain | undefined {
    return this.domains.find((domain) => domain.key === key);
  }

  findMarket(key: string): Market | undefined {
    return this.markets.find((market) => market.key === key);
  }

  findLanguage(key: string): Language | undefined {
    return this.languages.find((language) => language.key === key);
  }

  containsCover(resourceMedias: ResourceMedia[]): boolean {
    return resourceMedias?.filter((resourceMedia) => resourceMedia.isCover).length === 1;
  }

  getCover(resourceMedias: ResourceMedia[]): string {
    const resourceMedia = resourceMedias.find((resourceMedia2) => resourceMedia2.isCover);
    return resourceMedia?.media?.thumbnail ?? '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.unsubscribe();
  }

  private enrichDatasource(result: DatarefSearchResult): void {
    const enrichedContent = result.content.map((item) => ({
      ...item,
      coverImageUrl: this.getCover(item.medias),
    }));

    this.dataSource = new MatTableDataSource<any>(enrichedContent);
    this.dataSource.sort = this.matSort;
    this.totalElements = result.page.totalElements;
    this.pageIndex = result.page.number;
    this.updateDisplayInfo(this.totalElements, result.page.totalPages);
  }

  private createColumnConfig(): void {
    this.columnConfigs = [
      {
        name: 'actions',
        dataFieldName: 'key',
        headerTranslationKey: 'CONTENTS.SEARCH.ACTION',
        content: {
          type: 'action',
          actions: [
            {
              label: 'Edit',
              type: 'edit',
              icon: 'edit',
              callback: (element: PartnerSearchContent) => {
                globalThis.location.href = `/partners/edit/${element.key}`;
              },
            },
          ],
        },
        sortable: false,
      },
      {
        name: 'targetDomains',
        dataFieldName: 'targetDomains',
        headerTranslationKey: 'PARTNERS.SEARCH.TARGET_DOMAINS',
        content: {
          type: 'string',
          value: (element: PartnerSearchContent) => {
            const domains = element.targetDomains || [];
            const translatedDomains = domains.map((domain) => {
              const found = this.findDomain(domain.key);
              return this.refDataService.translateReference(found);
            });
            return translatedDomains
              .map((domain) => domain?.translatedName ?? '')
              .filter(Boolean)
              .join('<br>');
          },
        },
        sortable: false,
      },
      {
        name: 'media',
        dataFieldName: 'coverImageUrl',
        headerTranslationKey: 'PARTNERS.SEARCH.MEDIA',
        content: {
          type: 'string',
          value: (element: any) => element.coverImageUrl ?? '',
          isImage: true,
        },
        sortable: false,
      },
      {
        name: 'name',
        dataFieldName: 'name',
        headerTranslationKey: 'PARTNERS.SEARCH.LABEL',
        content: {
          type: 'string',
          value: (element: PartnerSearchContent) => {
            let result = element.key || '';
            if (element.details && Array.isArray(element.details)) {
              element.details.forEach((detail: PartnerDetail) => {
                const market = this.findMarket(detail.market);
                const language = this.findLanguage(detail.lang);
                const translatedMarket = this.refDataService.translateReference(market);
                const translatedLanguage = this.refDataService.translateReference(language);
                result += `<br><b>${translatedMarket?.translatedName ?? ''} / ${translatedLanguage?.translatedName ?? ''}</b>: ${
                  detail.name
                }`;
              });
            }
            return result;
          },
        },
        sortable: true,
      },
    ];
  }

  private updateDisplayInfo(totalElements: number, totalPages: number): void {
    const resultsMessage = `${totalElements} ${
      totalElements === 1
        ? this.translateService.instant('PARTNERS.SEARCH.RESULT_FOUND')
        : this.translateService.instant('PARTNERS.SEARCH.RESULTS_FOUND')
    }`;

    this.displayInfo = {
      ...this.displayInfo,
      dataLength: totalElements,
      resultsMessage,
      totalPages,
    };
  }
}
